/**
 * RTK Query endpoints for the dynamic policy builder (epic-6 §6, PB-1).
 *
 * Backends:
 *  - `POST /policies/validate-expression` + `POST /policies/evaluate` →
 *    authorization service (registered as `policies`; routed through the
 *    generic `/api` proxy, same as the existing apiSlice policy endpoints).
 *  - `GET /registry/services` → core registry (action autocomplete).
 *  - `GET /policies` → authorization list (parent-policy / base-policy select).
 */
import { apiSlice } from './apiSlice';
import type {
  PolicyExpressionValidation,
  PolicyEvaluateRequest,
  PolicyEvaluateResult,
  PolicySummary,
  RegistryService,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

export interface RegistryServiceResult {
  data: RegistryService[];
  total?: number;
}

export const policyBuilderApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /registry/services — registered services (RegistryService[]). */
    getRegistryServices: builder.query<RegistryService[], void>({
      query: () => '/registry/services',
      transformResponse: (response: unknown): RegistryService[] => {
        if (Array.isArray(response)) return response as RegistryService[];
        const wrapped = response as { data?: unknown } | null;
        if (wrapped && Array.isArray(wrapped.data)) {
          return wrapped.data as RegistryService[];
        }
        return [];
      },
      providesTags: ['Service'],
    }),

    /** POST /policies/validate-expression { expression } → { valid, error?, referencedFields? }. */
    validatePolicyExpression: builder.mutation<
      PolicyExpressionValidation,
      { expression: string }
    >({
      query: (body) => ({ url: '/policies/validate-expression', method: 'POST', body }),
    }),

    /** POST /policies/evaluate { userId, action, resource, context?, policyIds? } → { allowed, rateLimit? }. */
    evaluatePolicyRequest: builder.mutation<PolicyEvaluateResult, PolicyEvaluateRequest>({
      query: (body) => ({ url: '/policies/evaluate', method: 'POST', body }),
    }),

    /** GET /policies — usable as a parent/base-policy picker (paginated). */
    getPolicyOptions: builder.query<PolicySummary[], void>({
      query: () => ({ url: '/policies', params: { page: 1, pageSize: 100 } }),
      transformResponse: (response: unknown): PolicySummary[] => {
        if (Array.isArray(response)) return response as PolicySummary[];
        const wrapped = response as { data?: unknown } | null;
        if (wrapped && Array.isArray(wrapped.data)) {
          return wrapped.data as PolicySummary[];
        }
        return [];
      },
      providesTags: ['Policy'],
    }),

    /** GET /policies/user/:userId — policies owned by a specific user
     * (admin on-behalf; cross-owner read gated by create-on-behalf permission). */
    getPoliciesByUser: builder.query<PolicySummary[], string>({
      query: (userId) => ({ url: `/policies/user/${userId}` }),
      transformResponse: (response: unknown): PolicySummary[] => {
        if (Array.isArray(response)) return response as PolicySummary[];
        const wrapped = response as { data?: unknown } | null;
        if (wrapped && Array.isArray(wrapped.data)) {
          return wrapped.data as PolicySummary[];
        }
        return [];
      },
      providesTags: ['Policy'],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetRegistryServicesQuery,
  useValidatePolicyExpressionMutation,
  useEvaluatePolicyRequestMutation,
  useGetPolicyOptionsQuery,
  useGetPoliciesByUserQuery,
} = policyBuilderApi;